import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Flex, Spacer } from '@chakra-ui/react';

import DeleteAllButton from '../itemData/DeleteAllButton';

import {
    getAdjustmentsForInstrument,
    getValidAdjustmentRows,
    updateAdjustmentCells,
    deleteAdjustments,
    deleteAllAdjustments,
    validateParsedNewRows,
    formatParsedNewRows,
    columnSchema,
    createAdjustments,
} from '../../services/hedges';

import AddRowsButton from '../itemData/AddRowsButton';

import useTabularView from '../../hooks/useTabularView';
import useCommonToast from '../../hooks/useCommonToast';

import MainModal from '../../components/utils/MainModal';
import DataGrid from '../grid/DataGrid';
import DataGridWrapper from '../grid/DataGridWrapper';

const EditAdjustmentsModal = ({ instrumentId, ...rest }) => {
    const intl = useIntl();
    const toast = useCommonToast();

    const adjustmentsTabularView = useTabularView('instrumentAdjustmentId', 8);

    const { dateIntervals, linkedObjects } = useSelector((state) => state.adjustment);

    const getServerSideData = async (params) => {
        const page = params.request.startRow / 500 + 1;
        const sortModel = params.request.sortModel[0] || null;

        const payload = {
            page,
            sortColumn: sortModel ? sortModel.colId : null,
            sortByDescending: sortModel?.sort === 'desc',
        };

        const response = await getAdjustmentsForInstrument(instrumentId, payload);

        if (response.totalCount === 0) {
            params.api.showNoRowsOverlay();
        }

        params.success({ rowData: response.data, rowCount: response.totalCount });
    };

    const refreshServerSideData = () => {
        const { gridApi } = adjustmentsTabularView;

        gridApi.current.refreshServerSideStore({
            purge: true,
        });
    };

    // @todo when updateAdjustmentCells succeeds the getAdjustmentsForInstrument function is called several times
    const updateServerSideData = async (rowIds) => {
        const { gridApi } = adjustmentsTabularView;
        const rows = rowIds.map((id) => gridApi.current.getRowNode(id));
        const validRows = getValidAdjustmentRows(rows);

        if (validRows.length === 0) return;

        try {
            await updateAdjustmentCells(validRows);

            toast(intl.formatMessage({ id: 'hedge_adjustments_on_success' }));
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: intl.formatMessage({ id: 'common_generic_saving_error' }),
                });
            }
        }
    };

    const onDeleteRow = async (rowIds) => {
        const { gridApi, typeDataId } = adjustmentsTabularView;
        const adjustmentIds = rowIds.map((id) => gridApi.current.getRowNode(id).data[typeDataId]);

        if (adjustmentIds.length === 0) return;

        try {
            await deleteAdjustments(instrumentId, adjustmentIds);
            toast(intl.formatMessage({ id: 'common_delete_grid_msg' }, { n: adjustmentIds.length }));

            refreshServerSideData();
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: intl.formatMessage({ id: 'common_generic_delete_error' }),
                });
            }
        }
    };

    const addRows = async (newRows) => {
        const { addRows, setIsLoading } = adjustmentsTabularView;

        const successMessage = intl.formatMessage({ id: 'common_generic_item_change_success' });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        try {
            setIsLoading(true);
            const payload = newRows.map((row) => ({ instrumentId, ...row }));

            await createAdjustments(payload);

            refreshServerSideData();

            setIsLoading(false);
            addRows.onClose();

            toast(successMessage);
        } catch (error) {
            setIsLoading(false);

            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }
    };

    return (
        <MainModal
            isOpen
            onClose={() => {
                rest.onClose();
            }}
            header={<FormattedMessage id="hedge_adjustments_modal_title" />}
            content={
                <>
                    <Box my={4}>
                        <Flex mt={4} mb={12} align="end" wrap="wrap">
                            <Spacer display={{ base: 'none', sm: 'block' }} />

                            <DeleteAllButton
                                subItemId={instrumentId}
                                deleteCallback={deleteAllAdjustments}
                                refetchData={refreshServerSideData}
                                isInTabularTab={true}
                            />

                            <AddRowsButton
                                modal={adjustmentsTabularView.addRows}
                                isLoading={adjustmentsTabularView.isLoading}
                                validate={validateParsedNewRows}
                                format={formatParsedNewRows}
                                selectTypes={{ dateIntervals, linkedObjects }}
                                columns={[
                                    {
                                        field: 'startDate',
                                        headerName: intl.formatMessage({ id: 'common_grid_start_date' }),
                                    },
                                    {
                                        field: 'endDate',
                                        headerName: intl.formatMessage({ id: 'common_grid_end_date' }),
                                    },
                                    {
                                        field: 'scaler',
                                        headerName: intl.formatMessage({ id: 'hedge_adjustments_grid_scaler' }),
                                    },
                                    {
                                        field: 'adder',
                                        headerName: intl.formatMessage({ id: 'hedge_adjustments_grid_adder' }),
                                    },
                                    {
                                        field: 'min',
                                        headerName: intl.formatMessage({ id: 'hedge_adjustments_grid_min' }),
                                    },
                                    {
                                        field: 'max',
                                        headerName: intl.formatMessage({ id: 'hedge_adjustments_grid_max' }),
                                    },
                                    {
                                        field: 'dateIntervalName',
                                        headerName: intl.formatMessage({ id: 'hedge_adjustments_grid_date_interval' }),
                                    },
                                    {
                                        field: 'linkedObjectName',
                                        headerName: intl.formatMessage({ id: 'hedge_adjustments_grid_linked_object' }),
                                    },
                                ]}
                                columnSchema={{
                                    ...columnSchema,
                                    dateIntervals: {
                                        ...columnSchema.dateIntervals,
                                        constraints: dateIntervals.map((type) => type.description),
                                    },
                                    linkedObjects: {
                                        ...columnSchema.linkedObjects,
                                        constraints: linkedObjects.map((type) => type.description),
                                    },
                                }}
                                addRows={addRows}
                            />
                        </Flex>

                        <DataGridWrapper>
                            <DataGrid
                                serverSideDatasource={{ getRows: getServerSideData }}
                                onGridReady={(params) => (adjustmentsTabularView.gridApi.current = params.api)}
                                onDataChange={updateServerSideData}
                                onDelete={onDeleteRow}
                                rowModelType="serverSide"
                                serverSideStoreType="partial"
                                paginationPageSize={500}
                                cacheBlockSize={500}
                                columns={[
                                    {
                                        type: 'date',
                                        field: 'startDate',
                                        headerName: intl.formatMessage({ id: 'common_grid_start_date' }),
                                        sortable: true,
                                        sort: 'asc',
                                        minWidth: 250,
                                        cellEditorParams: { nullable: true },
                                    },
                                    {
                                        type: 'date',
                                        field: 'endDate',
                                        headerName: intl.formatMessage({ id: 'common_grid_end_date' }),
                                        sortable: true,
                                        minWidth: 250,
                                        cellEditorParams: { nullable: true },
                                    },
                                    {
                                        type: 'number',
                                        field: 'scaler',
                                        headerName: intl.formatMessage({ id: 'hedge_adjustments_grid_scaler' }),
                                        sortable: true,
                                    },
                                    {
                                        type: 'number',
                                        field: 'adder',
                                        headerName: intl.formatMessage({ id: 'hedge_adjustments_grid_adder' }),
                                        sortable: true,
                                    },
                                    {
                                        type: 'number',
                                        field: 'min',
                                        headerName: intl.formatMessage({ id: 'hedge_adjustments_grid_min' }),
                                        sortable: true,
                                    },
                                    {
                                        type: 'number',
                                        field: 'max',
                                        headerName: intl.formatMessage({ id: 'hedge_adjustments_grid_max' }),
                                        sortable: true,
                                    },
                                    {
                                        type: 'select',
                                        field: 'dateIntervalId',
                                        headerName: intl.formatMessage({ id: 'hedge_adjustments_grid_date_interval' }),
                                        sortable: false,
                                        minWidth: 150,
                                        cellEditorParams: { options: dateIntervals, required: false },
                                    },
                                    {
                                        type: 'select',
                                        field: 'linkedObjectId',
                                        headerName: intl.formatMessage({ id: 'hedge_adjustments_grid_linked_object' }),
                                        sortable: false,
                                        minWidth: 400,
                                        cellEditorParams: { options: linkedObjects, required: false },
                                    },
                                ]}
                                isInitialRequestSent={true}
                                noDataMessageId="hedge_adjustments_no_data"
                            />
                        </DataGridWrapper>
                    </Box>
                </>
            }
            secondaryButton={<></>}
            footerRightSlot={<></>}
        />
    );
};

export default EditAdjustmentsModal;
